import {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
    type ReactNode,
} from "react";
import Icon from "./Icon";

/* ── Types ── */
type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
    id: string;
    type: ToastType;
    message: string;
    duration: number;
}

interface ToastContextValue {
    toast: (message: string, type?: ToastType, duration?: number) => void;
}

/* ── Context ── */
const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
    return ctx;
}

/* ── Provider ── */
export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const counterRef = useRef(0);

    const addToast = useCallback(
        (message: string, type: ToastType = "info", duration = 3000) => {
            const id = `toast-${++counterRef.current}`;
            setToasts((prev) => [...prev, { id, type, message, duration }]);
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        },
        []
    );

    const dismiss = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toast: addToast }}>
            {children}

            {/* Toast container */}
            <div
                id="toast-container"
                className="fixed top-6 left-1/2 -translate-x-1/2 z-[200] flex flex-col items-center gap-2 w-full max-w-[430px] px-6 pointer-events-none"
            >
                {toasts.map((t) => (
                    <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

/* ── Single Toast ── */
const iconMap: Record<ToastType, string> = {
    success: "check-circle",
    error: "info-circle",
    info: "info-circle",
    warning: "info-circle",
};

const accentMap: Record<ToastType, string> = {
    success: "#4ade80",
    error: "#f87171",
    info: "#f3ce28",
    warning: "#fbbf24",
};

function ToastItem({
    toast,
    onDismiss,
}: {
    toast: Toast;
    onDismiss: (id: string) => void;
}): ReactNode {
    const accent = accentMap[toast.type];

    return (
        <div
            className="pointer-events-auto w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-[#1a1a1a]/90 backdrop-blur-xl shadow-lg animate-[slideDown_0.3s_ease-out]"
            style={{ borderLeftColor: accent, borderLeftWidth: 3 }}
            onClick={() => onDismiss(toast.id)}
        >
            <Icon name={iconMap[toast.type]} size={20} className="shrink-0 opacity-90" />
            <span className="text-sm font-semibold text-white/90 flex-1">{toast.message}</span>
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="shrink-0 cursor-pointer text-white/40 hover:text-white/70 transition-colors"
            >
                <path
                    d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
            </svg>
        </div>
    );
}
